import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import DynamicRendering from '@adv-mt/mt-perf-dynamic-rendering'
import SharedCorporateFooter, {sharedCorporateFooterHeights} from '@adv-mt/mt-shared-corporate-footer'
import MotorSharedTopbar from '@adv-mt/mt-shared-topbar'

import LayoutBasicContainer from './components/Container/index.js'
import LayoutFullHeight from './components/FullHeight/index.js'
import LayoutFullWidth from './components/FullWidth/index.js'
import {LAYOUT_BASIC_BASE_CLASS_NAME, MARGIN_TOP_SIZES} from './settings.js'

const LayoutBasic = ({
  as: Container = 'div',
  children,
  footerProps = {},
  isFooterless = false,
  isHeaderFlush = false,
  isHeaderless = false,
  isMarginless = false,
  isPaddingless = false,
  linkFactory,
  topbarProps = {}
}) => {
  const {
    domain,
    browser: {deviceType}
  } = useContext(Context)
  const config = domain.get('config')

  const appName = config.get('APP_NAME')
  const isWebview = config.get('isWebview')

  const footerHeight = sharedCorporateFooterHeights[appName][deviceType]

  const showHeader = !isHeaderless && !isWebview
  const showFooter = !isFooterless && !isWebview

  const className = cx(LAYOUT_BASIC_BASE_CLASS_NAME, {
    [`${LAYOUT_BASIC_BASE_CLASS_NAME}--headerless`]: !showHeader
  })

  return (
    <div className={className}>
      {showHeader && <MotorSharedTopbar isHeaderFlush={isHeaderFlush} linkFactory={linkFactory} {...topbarProps} />}
      <LayoutBasicContainer
        as={Container}
        isPaddingless={isPaddingless || isWebview}
        marginTopSize={isMarginless || isWebview ? MARGIN_TOP_SIZES.NONE : MARGIN_TOP_SIZES.MEDIUM}
      >
        {children}
      </LayoutBasicContainer>
      {showFooter && (
        <DynamicRendering height={footerHeight}>
          <SharedCorporateFooter linkFactory={linkFactory} {...footerProps} />
        </DynamicRendering>
      )}
    </div>
  )
}

LayoutBasic.Basic = LayoutBasic
LayoutBasic.Container = LayoutBasicContainer
LayoutBasic.FullHeight = LayoutFullHeight
LayoutBasic.FullWidth = LayoutFullWidth

LayoutBasic.propTypes = {
  /** The component to render the container */
  as: PropTypes.elementType,

  /** The children */
  children: PropTypes.element,

  /** True for use a footerless version of basic layout */
  isFooterless: PropTypes.bool,

  /** True to remove the bottom border radius of the header */
  isHeaderFlush: PropTypes.bool,

  /** True for use a headerless version of basic layout */
  isHeaderless: PropTypes.bool,

  /** True for remove top margin of basic layout */
  isMarginless: PropTypes.bool,

  /** True for remove left and right paddings of basic layout */
  isPaddingless: PropTypes.bool,

  /** linkfactory component */
  linkFactory: PropTypes.func,

  /** Props to pass to MotorSharedTopbar */
  topbarProps: PropTypes.shape({
    linkFactory: PropTypes.func.isRequired
  }),

  /** Props to pass to SharedCorporateFooter */
  footerProps: PropTypes.shape({
    linkFactory: PropTypes.func.isRequired
  })
}

export {
  BACKGROUND_COLORS as layoutBasicBackgroundColors,
  MARGIN_TOP_SIZES as layoutBasicMarginTopSizes,
  MAX_CONTENT_WIDTHS as layoutBasicMaxContentWidths
} from './settings.js'

export default LayoutBasic
